import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  GAME_ERROR,
  pickLang,
  type EventTexts,
  type Game,
  type GameObject,
  type GameStateResponse,
  type MissionSummary,
} from "@shared";
import { ApiFetchError } from "../api/client";
import { useSession } from "../auth/SessionProvider";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { currentLang } from "../i18n";
import { playFindSound, vibrateFind } from "./findFeedback";
import * as api from "./gameApi";
import HomeScreen from "./HomeScreen";
import Mission1Screen from "./Mission1Screen";
import Mission2Screen from "./Mission2Screen";
import WelcomeScreen from "./WelcomeScreen";

/**
 * Aiguilleur du jeu : il charge l'état, tient les actions, et choisit l'écran.
 *
 *   mot de bienvenue  →  accueil  →  mission en cours  →  bilan  →  accueil
 *
 * Les écrans eux-mêmes ne savent rien du serveur : ils reçoivent des données et
 * remontent des intentions. C'est ce qui permet d'en ajouter un (la mission 2)
 * sans toucher à la mécanique.
 */
export default function GameScreen() {
  const { t } = useTranslation();
  const { user, logout } = useSession();
  const lang = currentLang();

  const [state, setState] = useState<GameStateResponse | null>(null);
  const [summary, setSummary] = useState<MissionSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [justFound, setJustFound] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Le jeune a voulu cocher un 11e objet : on lui explique, dans une fenêtre. */
  const [limitReached, setLimitReached] = useState(false);
  const [welcomeDone, setWelcomeDone] = useState(false);
  /** Relecture volontaire du mot de bienvenue depuis l'accueil. */
  const [rereading, setRereading] = useState(false);

  const reload = useCallback(async () => {
    try {
      setState(await api.fetchState());
    } catch {
      setError("game.error.offline");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function startMission(game: Game): Promise<void> {
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      await api.startMission(game);
      await reload();
    } catch {
      setError("game.error.startFailed");
    } finally {
      setBusy(false);
    }
  }

  async function finishMission(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setSummary(await api.finishMission());
      await reload();
    } catch {
      setError("game.error.finishFailed");
    } finally {
      setBusy(false);
    }
  }

  async function toggle(object: GameObject): Promise<void> {
    // Bascule optimiste : la carte réagit tout de suite, sans attendre le
    // serveur. En cas d'échec on recharge, ce qui remet l'état réel.
    const next = !object.found;

    // Au plus autant de coches que d'objets à trouver. Décocher reste toujours
    // possible : c'est ce qui libère une place. Le serveur applique la même
    // limite ; ici on évite seulement un aller-retour et un geste « annulé ».
    if (next && state !== null && state.found >= state.objectsToFind) {
      setLimitReached(true);
      return;
    }

    setState((current) =>
      current === null
        ? current
        : {
            ...current,
            objects: current.objects.map((o) => (o.id === object.id ? { ...o, found: next } : o)),
            found: current.found + (next ? 1 : -1),
          },
    );

    if (next) {
      setJustFound(object.id);
      playFindSound();
      vibrateFind();
      window.setTimeout(() => {
        setJustFound((id) => (id === object.id ? null : id));
      }, 700);
    }

    try {
      if (next) await api.addFind(object.id);
      else await api.removeFind(object.id);
    } catch (e) {
      // Limite atteinte côté serveur (autre onglet, état périmé) : ce n'est pas
      // une panne réseau, on remet l'état réel et on explique.
      if (e instanceof ApiFetchError && e.code === GAME_ERROR.tooManyFinds) {
        setLimitReached(true);
      } else {
        setError("game.error.offline");
      }
      await reload();
    }
  }

  if (state === null) {
    return (
      <main className="screen screen--centered">
        <p className="card__text card__text--muted">{t("common.loading")}</p>
      </main>
    );
  }

  // --- Mot de bienvenue : première connexion, ou relecture volontaire --------
  if (state.texts !== null && (rereading || (!state.welcomeSeen && !welcomeDone))) {
    return (
      <WelcomeScreen
        texts={state.texts}
        rereading={rereading}
        onClose={() => {
          setRereading(false);
        }}
        onAccepted={() => {
          setWelcomeDone(true);
          void reload();
        }}
      />
    );
  }

  // --- Aucune mission ouverte par l'admin ------------------------------------
  if (state.event === null || state.texts === null) {
    return (
      <main className="screen">
        <Header pseudo={user?.pseudo ?? ""} texts={state.texts} />
        <div className="card">
          <h2 className="card__title">{t("child.welcome")}</h2>
          <p className="card__text card__text--muted">{t("child.comingSoon")}</p>
        </div>
        <Footer onLogout={() => void logout()} />
      </main>
    );
  }

  const running = state.mission !== null && state.mission.endedAt === null;

  // --- Une partie en cours : l'écran dépend du jeu ---------------------------
  if (running && state.mission !== null) {
    // Le titre vient des textes de l'admin, pas d'une chaîne codée en dur.
    const title = pickLang(
      state.mission.game === "pairing" ? state.texts.mission2_title : state.texts.mission1_title,
      lang,
    );

    return state.mission.game === "pairing" ? (
      <Mission2Screen
        title={title}
        missionId={state.mission.id}
        startedAt={state.mission.startedAt}
        busy={busy}
        onFinish={() => void finishMission()}
      />
    ) : (
      <Mission1Screen
        title={title}
        state={state}
        justFound={justFound}
        busy={busy}
        errorKey={error}
        limitReached={limitReached}
        onCloseLimit={() => {
          setLimitReached(false);
        }}
        onToggle={(object) => void toggle(object)}
        onFinish={() => void finishMission()}
      />
    );
  }

  // --- Bilan, juste après le clic sur « Fin » -------------------------------
  if (summary !== null) {
    return (
      <main className="screen">
        <Header pseudo={user?.pseudo ?? ""} texts={state.texts} />
        <div className="card card--summary">
          <p className="summary__badge">{summary.completed ? "🏆" : "🚀"}</p>
          <h2 className="card__title">
            {summary.completed ? t("game.summary.title") : t("game.summary.titleIncomplete")}
          </h2>
          <p className="summary__line">
            {t("game.summary.duration")} <strong>{api.formatDuration(summary.durationSeconds)}</strong>
            <br />
            {t("game.summary.found", { count: summary.found, total: summary.total })}
            {/* Dit combien de faux objets ont été cochés, sans dire lesquels :
                le jeune doit pouvoir réessayer en cherchant, pas en lisant la
                réponse. */}
            {summary.wrong > 0 && (
              <>
                <br />
                {t("game.summary.wrong", { count: summary.wrong })}
              </>
            )}
            <br />
            <span className="summary__percent">{summary.percent} %</span>
          </p>
          <button
            type="button"
            className="button button--primary button--full"
            onClick={() => {
              setSummary(null);
            }}
          >
            {t("game.backHome")}
          </button>
        </div>
        <Footer onLogout={() => void logout()} />
      </main>
    );
  }

  // --- Accueil : le point d'entrée des deux missions -------------------------
  return (
    <main className="screen">
      <Header pseudo={user?.pseudo ?? ""} texts={state.texts} />

      {error !== null && <p className="form__error">{t(error)}</p>}
      {state.total === 0 && <p className="panel__hint">{t("game.empty")}</p>}

      <HomeScreen
        texts={state.texts}
        results={state.results}
        busy={busy}
        mission1Available={state.total > 0}
        contest={state.contest}
        onStart={(game) => void startMission(game)}
        onRereadWelcome={() => {
          setRereading(true);
        }}
        // Recharge l'état complet plutôt que de basculer un drapeau local :
        // c'est le serveur qui décide si la participation est enregistrée.
        onContestEntered={() => void reload()}
      />

      <Footer onLogout={() => void logout()} />
    </main>
  );
}

function Header({ pseudo, texts }: { pseudo: string; texts: EventTexts | null }) {
  const { t } = useTranslation();
  const lang = currentLang();
  // Titre défini par l'admin ; à défaut, la traduction embarquée.
  const title = texts !== null ? pickLang(texts.app_title, lang) : t("app.title");
  return (
    <header className="screen__header">
      <h1 className="screen__title">{title}</h1>
      {pseudo !== "" && <p className="screen__tagline">{t("child.greeting", { pseudo })}</p>}
    </header>
  );
}

function Footer({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation();
  return (
    <footer className="screen__footer">
      <button type="button" className="button button--ghost button--small" onClick={onLogout}>
        {t("child.logout")}
      </button>
      <LanguageSwitcher />
    </footer>
  );
}
